<script lang="ts" setup>
  import {
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import DatamapperTableRow from '@/components/app/cases/DatamapperTableRow.vue'
  import { computed, type PropType, ref, watch } from 'vue'
  import { useIntersectionObserver } from '@vueuse/core'
  import type {
    CustomerCase,
    CustomerCaseDataChange,
    CustomerEntityWithRelationship,
    CustomerEntityChange,
    TDataMapper,
    ProposedEntityChange
  } from '@/types/generated'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { ChevronUp, ChevronDown } from 'lucide-vue-next'
  import { yup } from '@/lib/validation'

  const RENDER_CHUNK_SIZE = 40

  const { t } = useTranslation({
    en: {
      loadingData: 'Loading data',
      rechtsgebiet: 'Legal area',
      feld: 'Field',
      wert: 'Value',
      quelle: 'Source',
      aktualisiert: 'Updated',
      nurFelderMitWertenAnzeigen: 'Show only fields with values',
      updatingData: 'Updating data',
      selectAllExportFieldsAria: 'Select or deselect all fields for export',
      noFieldsToDisplay: 'No fields to display'
    },
    de: {
      loadingData: 'Lade Daten',
      rechtsgebiet: 'Rechtsgebiet',
      feld: 'Feld',
      wert: 'Wert',
      quelle: 'Quelle',
      aktualisiert: 'Aktualisiert',
      nurFelderMitWertenAnzeigen: 'Nur Felder mit Werten anzeigen',
      updatingData: 'Daten werden aktualisiert',
      selectAllExportFieldsAria: 'Alle Felder für den Export auswählen oder abwählen',
      noFieldsToDisplay: 'Keine Felder zum Anzeigen'
    }
  })

  const props = defineProps({
    isEntity: {
      type: Boolean,
      required: false,
      default: false
    },
    mappers: {
      type: Object as PropType<TDataMapper[]>,
      required: true
    },
    object: {
      type: Object as PropType<CustomerEntityWithRelationship | CustomerCase>,
      required: true
    },
    showKeys: {
      type: Array as PropType<string[]>,
      required: true
    },
    mandatoryKeys: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => []
    },
    customValidators: {
      type: Object as PropType<Record<string, yup.AnySchema> | null>,
      required: false,
      default: () => {}
    },
    mode: {
      type: String as PropType<'view' | 'edit'>,
      required: false,
      default: 'view'
    },
    ghost: {
      type: Boolean,
      required: false,
      default: false
    },
    sorting: {
      type: Boolean,
      required: false,
      default: true
    },
    exportMode: {
      type: Boolean,
      required: false,
      default: false
    },
    exportSelectedKeys: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => []
    },
    progressiveRendering: {
      type: Boolean,
      required: false,
      default: false
    }
  })

  const emit = defineEmits([
    'onValueChange',
    'valueChangeError',
    'acceptProposedChange',
    'rejectProposedChange',
    'update:exportSelectedKeys'
  ])

  const safeCustomValidators = computed(() => props.customValidators || {})

  const exportSelectedSet = computed(() => new Set(props.exportSelectedKeys || []))

  const areAllExportKeysSelected = computed(() => {
    if (!props.exportMode || props.showKeys.length === 0) return false
    return props.showKeys.every((key) => exportSelectedSet.value.has(key))
  })

  const exportHeaderCheckboxIndeterminate = computed(() => {
    if (!props.exportMode || props.showKeys.length === 0) return false
    const count = props.showKeys.filter((key) => exportSelectedSet.value.has(key)).length
    return count > 0 && count < props.showKeys.length
  })

  const isExportKeySelected = (mapperKey: string) => exportSelectedSet.value.has(mapperKey)

  const selectAllForExport = () => {
    if (!props.exportMode) return
    emit('update:exportSelectedKeys', [...props.showKeys])
  }

  const deselectAllForExport = () => {
    if (!props.exportMode) return
    emit('update:exportSelectedKeys', [])
  }

  const handleToggleExportSelection = (mapperKey: string, selected: boolean) => {
    if (!props.exportMode) return
    const next = new Set(props.exportSelectedKeys || [])
    if (selected) {
      next.add(mapperKey)
    } else {
      next.delete(mapperKey)
    }
    emit('update:exportSelectedKeys', [...next])
  }

  const onHeaderExportSelectAllChange = (checked: boolean) => {
    if (!props.exportMode) return
    if (checked) {
      selectAllForExport()
    } else {
      deselectAllForExport()
    }
  }

  // Sorting state
  const sortField = ref<string>('aktualisiert')
  const sortDirection = ref<'asc' | 'desc'>('desc')

  const changes = computed<CustomerEntityChange[] | CustomerCaseDataChange[]>(() => {
    if (props.isEntity) {
      return props.object?.entity?.changes || []
    }
    return props.object?.data?.changes || []
  })

  const proposedChanges = computed<ProposedEntityChange[]>(() => {
    return (props.object as CustomerEntityWithRelationship)?.entity?.proposed_changes || []
  })

  const showTable = computed<boolean>(() => {
    return !!props.object && !!props.mappers && props.mappers.length > 0
  })

  const tableColumnCount = computed(() => {
    let count = 4
    if (!props.isEntity) {
      count += 1
    }
    if (props.exportMode) {
      count += 1
    }
    return count
  })

  const skeletonItems = computed<string[]>(() => {
    if (props.showKeys.length > 0) {
      return Array.from({ length: props.showKeys.length }, (_, index) => `item-${index}`)
    }
    return Array.from({ length: 10 }, (_, index) => `item-${index}`)
  })

  const onValueChange = (key: string, mapperKey: string, value: any) => {
    emit('onValueChange', key, mapperKey, value)
  }
  const handleValueChangeError = (mapperKey: string, isError: boolean) => {
    emit('valueChangeError', mapperKey, isError)
  }

  const handleAcceptProposedChange = (proposedChange: ProposedEntityChange) => {
    emit('acceptProposedChange', proposedChange)
  }

  const handleRejectProposedChange = (proposedChange: ProposedEntityChange) => {
    emit('rejectProposedChange', proposedChange)
  }
  // Get latest change date for each mapper key
  const getLatestChangeDate = (mapperKey: string): Date | null => {
    if (!changes.value) return null

    const relevantChanges = props.isEntity
      ? changes.value.filter((change: any) => change.entities_mapper_key === mapperKey)
      : changes.value.filter((change: any) => change.mapper_key === mapperKey)

    if (relevantChanges.length === 0) return null

    const latestChange = relevantChanges.sort(
      (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]

    return latestChange ? new Date(latestChange.created_at) : null
  }

  // Sort the mapper keys based on the current sort field and direction
  const sortedShowKeys = computed(() => {
    // Use Set to avoid duplicates and prevent empty values after sorting while showing all mappers
    if (sortField.value !== 'aktualisiert') {
      return new Set(props.showKeys)
    }

    const sortedArray = [...props.showKeys].sort((a, b) => {
      const dateA = getLatestChangeDate(a)
      const dateB = getLatestChangeDate(b)

      // Handle cases where one or both dates are null
      if (!dateA && !dateB) return 0
      if (!dateA) return sortDirection.value === 'asc' ? -1 : 1
      if (!dateB) return sortDirection.value === 'asc' ? 1 : -1

      // Sort by date
      const comparison = dateA.getTime() - dateB.getTime()
      return sortDirection.value === 'asc' ? comparison : -comparison
    })

    return new Set(sortedArray)
  })

  const activeShowKeys = computed<string[]>(() =>
    props.sorting ? Array.from(sortedShowKeys.value) : props.showKeys
  )

  const renderedRowCount = ref(RENDER_CHUNK_SIZE)

  const visibleShowKeys = computed<string[]>(() =>
    props.progressiveRendering
      ? activeShowKeys.value.slice(0, renderedRowCount.value)
      : activeShowKeys.value
  )

  const hasMoreRowsToRender = computed<boolean>(
    () => props.progressiveRendering && renderedRowCount.value < activeShowKeys.value.length
  )

  watch([() => props.showKeys.length, sortField, sortDirection], () => {
    if (props.progressiveRendering) {
      renderedRowCount.value = RENDER_CHUNK_SIZE
    }
  })

  const renderSentinel = ref<HTMLElement | null>(null)

  useIntersectionObserver(
    renderSentinel,
    ([entry]) => {
      if (entry?.isIntersecting && hasMoreRowsToRender.value) {
        renderedRowCount.value = Math.min(
          renderedRowCount.value + RENDER_CHUNK_SIZE,
          activeShowKeys.value.length
        )
      }
    },
    { rootMargin: '400px 0px' }
  )

  // Toggle sort when clicking on a sortable column header
  const toggleSort = (field: string) => {
    if (sortField.value === field) {
      // Toggle direction if already sorting by this field
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    } else {
      // Set new sort field and default to descending (newest first)
      sortField.value = field
      sortDirection.value = 'desc'
    }
  }
</script>
<template>
  <div>
    <Table
      :class="[
        'w-full table-fixed',
        {
          [`[&>div]:!border-0 [&_table]:border-separate [&_td]:!border-0 [&_th]:!border-0
          [&_tr:nth-child(even)]:bg-slate-50 [&_tr]:!border-0`]: ghost
        }
      ]"
    >
      <TableHeader
        v-if="!ghost"
        :class="{ 'h-6': ghost }"
      >
        <TableRow>
          <TableCell
            v-if="exportMode"
            :class="['w-12 shrink-0 p-2 align-middle', { '!h-6 !p-1': ghost }]"
          >
            <CheckboxField
              v-if="showKeys.length > 0"
              name="datamapper-export-select-all"
              :model-value="areAllExportKeysSelected"
              :indeterminate="exportHeaderCheckboxIndeterminate"
              :aria-label="t('selectAllExportFieldsAria')"
              data-cy="datamapper-export-select-all"
              @update:model-value="(v) => onHeaderExportSelectAllChange(v === true)"
            />
          </TableCell>
          <TableCell
            v-if="!isEntity"
            :class="['w-[10%]', { '!h-6 !p-1': ghost }]"
          >
            {{ t('rechtsgebiet') }}
          </TableCell>
          <TableCell
            :class="[isEntity ? 'w-[15%]' : 'w-[30%]', 'font-medium', { '!h-6 !p-1': ghost }]"
          >
            {{ t('feld') }}
          </TableCell>
          <TableCell :class="[isEntity ? 'w-[40%]' : 'w-[25%]', { '!h-6 !p-1': ghost }]">
            {{ t('wert') }}
          </TableCell>
          <TableCell :class="[isEntity ? 'w-[20%]' : 'w-[25%]', { '!h-6 !p-1': ghost }]">
            {{ t('quelle') }}
          </TableCell>
          <TableCell
            :class="[isEntity ? 'w-[10%]' : 'w-[10%]', { '!h-6 !p-1': ghost }, 'cursor-pointer']"
            @click="toggleSort('aktualisiert')"
          >
            <div class="flex items-center">
              {{ t('aktualisiert') }}
              <span class="ml-1">
                <ChevronUp
                  v-if="sortField === 'aktualisiert' && sortDirection === 'asc'"
                  class="h-4 w-4"
                />
                <ChevronDown
                  v-else
                  class="h-4 w-4"
                />
              </span>
            </div>
          </TableCell>
        </TableRow>
      </TableHeader>
      <TableBody v-if="showTable && showKeys.length > 0">
        <DatamapperTableRow
          v-for="mapper in visibleShowKeys"
          :key="mapper"
          :object="object"
          :is-entity="isEntity"
          :is-mandatory="mandatoryKeys.includes(mapper)"
          :custom-validator="safeCustomValidators[mapper]"
          :all-changes="changes"
          :proposed-changes="proposedChanges"
          :mapper-key="mapper"
          :mappers="mappers"
          :mode="mode"
          :export-mode="exportMode"
          :export-row-selected="exportMode && isExportKeySelected(mapper)"
          @on-value-change="onValueChange"
          @value-change-error="handleValueChangeError"
          @accept-proposed-change="handleAcceptProposedChange"
          @reject-proposed-change="handleRejectProposedChange"
          @toggle-export-selection="handleToggleExportSelection"
        />
        <TableRow
          v-if="hasMoreRowsToRender"
          aria-hidden="true"
          data-cy="datamapperTableLoadMoreSentinel"
        >
          <TableCell
            :colspan="tableColumnCount"
            class="p-0"
          >
            <div
              ref="renderSentinel"
              class="h-1 w-full"
            />
          </TableCell>
        </TableRow>
      </TableBody>
      <TableBody v-else-if="showTable && showKeys.length === 0">
        <TableEmpty
          :colspan="tableColumnCount"
          class="text-muted-foreground"
        >
          {{ t('noFieldsToDisplay') }}
        </TableEmpty>
      </TableBody>
      <TableBody v-else>
        <TableRow
          v-for="item in skeletonItems"
          :key="item"
        >
          <TableCell
            v-if="exportMode"
            class="h-[72px] w-12"
          >
            <SkeletonLoader class="mx-auto h-4 w-4" />
          </TableCell>
          <TableCell
            v-if="!isEntity"
            class="h-[72px] w-[10%]"
          >
            <SkeletonLoader class="h-4 w-16" />
          </TableCell>
          <TableCell :class="[isEntity ? 'w-[15%]' : 'w-[30%]', 'h-[72px]']">
            <SkeletonLoader class="h-4 w-16" />
          </TableCell>
          <TableCell :class="[isEntity ? 'w-[40%]' : 'w-[25%]', 'h-[72px]']">
            <SkeletonLoader class="h-4 w-32" />
          </TableCell>
          <TableCell :class="[isEntity ? 'w-[20%]' : 'w-[25%]', 'h-[72px]']">
            <SkeletonLoader class="h-4 w-24" />
          </TableCell>
          <TableCell :class="[isEntity ? 'w-[10%]' : 'w-[10%]', 'h-[72px]']">
            <SkeletonLoader class="h-4 w-16" />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
